import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { API } from '@/App';

// Default settings (used while loading or on error)
const DEFAULT_SETTINGS = {
  trial: {
    days: 90,
    daily_limit_minutes: 60,
    features: [
      "All Pro features included",
      "AI-powered learning",
      "Practice tests",
      "Study plans",
      "Progress tracking",
      "No credit card required"
    ]
  },
  pricing: {
    starter: { price_per_subject: 599, daily_limit_minutes: 60, daily_limit_display: "60 min/day" },
    plus: { price_per_subject: 799, daily_limit_minutes: 120, daily_limit_display: "120 min/day" },
    pro: { price_per_subject: 999, daily_limit_minutes: -1, daily_limit_display: "Unlimited" },
    discounts: { sibling_percent: 10, annual_percent: 20 }
  },
  policies: {
    grace_period_days: 7,
    refund_period_days: 7
  },
  support: {
    email: "support@contentiumtech.com",
    phone: "+91-XXXXXXXXXX",
    response_hours: 24
  }
};

// Cache duration (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

// Global cache to share across all hook instances
let globalSettingsCache = {
  data: null,
  timestamp: 0,
  promise: null
};

/**
 * Fetch settings with caching
 * Multiple components calling this will share the same fetch promise
 */
async function fetchSettingsWithCache(forceRefresh = false) {
  const now = Date.now();
  
  // Return cached data if valid and not forcing refresh
  if (!forceRefresh && globalSettingsCache.data && (now - globalSettingsCache.timestamp < CACHE_DURATION)) {
    return globalSettingsCache.data;
  }
  
  // If a fetch is already in progress, return that promise
  if (globalSettingsCache.promise) {
    return globalSettingsCache.promise;
  }
  
  // Start new fetch
  globalSettingsCache.promise = (async () => {
    try {
      const response = await fetch(`${API}/admin/public/trial-info`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const settings = {
            trial: data.trial || DEFAULT_SETTINGS.trial,
            pricing: data.pricing || DEFAULT_SETTINGS.pricing,
            policies: data.policies || DEFAULT_SETTINGS.policies,
            support: data.support || DEFAULT_SETTINGS.support
          };
          globalSettingsCache.data = settings;
          globalSettingsCache.timestamp = Date.now();
          return settings;
        }
      }
      return DEFAULT_SETTINGS;
    } catch (error) {
      console.error('Failed to fetch app settings:', error);
      return DEFAULT_SETTINGS;
    } finally {
      globalSettingsCache.promise = null;
    }
  })();
  
  return globalSettingsCache.promise;
}

/**
 * Clear the settings cache (useful when admin updates settings)
 */
export function clearSettingsCache() {
  globalSettingsCache = {
    data: null,
    timestamp: 0,
    promise: null
  };
}

// Context for app-wide settings
const SettingsContext = createContext(null);

/**
 * Provider component for settings - wrap your app with this for optimal caching
 */
export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(globalSettingsCache.data || DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(!globalSettingsCache.data);
  
  const refreshSettings = useCallback(async () => {
    setLoading(true);
    const data = await fetchSettingsWithCache(true);
    setSettings(data);
    setLoading(false);
  }, []);
  
  useEffect(() => {
    fetchSettingsWithCache().then(data => {
      setSettings(data);
      setLoading(false);
    });
  }, []);
  
  return (
    <SettingsContext.Provider value={{ settings, loading, refreshSettings }}>
      {children}
    </SettingsContext.Provider>
  );
}

/**
 * Hook to fetch and use all public settings from the backend
 * Settings are cached globally and shared across all components
 */
export function useAppSettings() {
  // Try to use context first (if inside SettingsProvider)
  const context = useContext(SettingsContext);
  
  const [localSettings, setLocalSettings] = useState(globalSettingsCache.data || DEFAULT_SETTINGS);
  const [localLoading, setLocalLoading] = useState(!globalSettingsCache.data);
  
  const localRefresh = useCallback(async () => {
    setLocalLoading(true);
    const data = await fetchSettingsWithCache(true);
    setLocalSettings(data);
    setLocalLoading(false);
  }, []);
  
  useEffect(() => {
    if (!context) {
      fetchSettingsWithCache().then(data => {
        setLocalSettings(data);
        setLocalLoading(false);
      });
    }
  }, [context]);
  
  if (context) {
    return context;
  }
  
  return { 
    settings: localSettings, 
    loading: localLoading,
    refreshSettings: localRefresh
  };
}

/**
 * Simplified hook for just trial info (backward compatibility)
 */
export function useTrialInfo() {
  const { settings, loading } = useAppSettings();
  
  return {
    trialDays: settings.trial.days,
    trialFeatures: settings.trial.features,
    gracePeriodDays: settings.policies.grace_period_days,
    loading
  };
}

/**
 * Hook for pricing information
 */
export function usePricing() {
  const { settings, loading } = useAppSettings();
  
  return {
    starter: settings.pricing.starter,
    plus: settings.pricing.plus,
    pro: settings.pricing.pro,
    discounts: settings.pricing.discounts,
    loading
  };
}

/**
 * Hook for support/contact information
 */
export function useSupport() {
  const { settings, loading } = useAppSettings();
  
  return {
    email: settings.support.email,
    phone: settings.support.phone,
    responseHours: settings.support.response_hours,
    loading
  };
}

/**
 * Hook for policy information (refunds, grace period)
 */
export function usePolicies() {
  const { settings, loading } = useAppSettings();
  
  return {
    gracePeriodDays: settings.policies.grace_period_days,
    refundPeriodDays: settings.policies.refund_period_days,
    loading
  };
}

/**
 * Format price for display (e.g., 599 -> "₹599")
 */
export function formatPrice(price) {
  return `₹${price?.toLocaleString('en-IN') || '0'}`;
}

/**
 * Format daily limit for display
 */
export function formatDailyLimit(minutes) {
  if (minutes === -1) return "Unlimited";
  if (minutes >= 60) return `${minutes / 60} hr/day`;
  return `${minutes} min/day`;
}
